import React, { useEffect, useState, useCallback, useRef } from 'react';
import { merchantApi } from '../../api/config';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus, faEdit, faBan, faCheck, faUsers, faWallet,
  faUserShield, faChartBar, faArrowDown, faArrowUp, faRotate,
  faCoins, faMoneyBillWave
} from '@fortawesome/free-solid-svg-icons';
import DataTable, { Column } from '../../components/shared/DataTable';
import { swalConfirm } from '../../components/shared/swalConfirm';
import {
  UiPageHeader, UiStatsGrid, UiFilterPanel, UiModal, UiButton, StatusBadge, UiInput, UiSelect,
} from '../../components/ui';
import type { StatCardDef, StatusDef } from '../../components/ui';
import dayjs from 'dayjs';

// ─── Types ────────────────────────────────────────────────────────────────────

interface SubReseller {
  id: string;
  email: string;
  name: string;
  company_name: string;
  phone: string;
  fee_rate: number;
  payout_fee_rate: number;
  p2p_fee_rate: number | null;
  p2p_payout_fee_rate: number | null;
  is_active: boolean;
  created_at: string;
  wallet_balance: number;
  merchant_count: number;
  payin_spread: number;
  payout_spread: number;
}

interface CommissionRow {
  id: string;
  created_at: string;
  transaction_type: 'parent_commission' | 'parent_payout_commission';
  amount: number;
  description: string;
  sub_reseller: { id: string; name: string; email: string; fee_rate: number; payout_fee_rate: number } | null;
  order: { id: string; order_number: string; base_amount: number } | null;
  payout: { id: string; payout_number: string; amount: number } | null;
  merchant: { id: string; name: string } | null;
}

interface CommissionSummary { total_amount: number; payin_amount: number; payout_amount: number; }

// ─── Constants ────────────────────────────────────────────────────────────────

const emptyForm = { email: '', password: '', name: '', company_name: '', phone: '', fee_rate: '', payout_fee_rate: '', p2p_fee_rate: '', p2p_payout_fee_rate: '' };

const formatCurrency = (v: number) =>
  new Intl.NumberFormat('th-TH', { minimumFractionDigits: 2 }).format(v);

const SUB_STATUS_MAP: Record<string, StatusDef> = {
  active: { bg: 'bg-green-100 dark:bg-green-500/15', text: 'text-green-700 dark:text-green-400', icon: faCheck, label: 'Active' },
  inactive: { bg: 'bg-gray-100 dark:bg-slate-700', text: 'text-gray-500 dark:text-slate-300', icon: faBan, label: 'Inactive' },
};

const COMM_TYPE_MAP: Record<string, StatusDef> = {
  parent_commission: { bg: 'bg-blue-100 dark:bg-blue-500/15', text: 'text-blue-700 dark:text-blue-400', icon: faArrowDown, label: 'P.In' },
  parent_payout_commission: { bg: 'bg-orange-100 dark:bg-orange-500/15', text: 'text-orange-700 dark:text-orange-400', icon: faArrowUp, label: 'P.Out' },
};

const TAB_BTN_BASE = 'inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all';
const TAB_BTN_ACTIVE = 'bg-white dark:bg-slate-600 text-gray-800 dark:text-slate-100 shadow-sm';
const TAB_BTN_INACTIVE = 'text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200';

// ─── Component ────────────────────────────────────────────────────────────────

const ResellerSubResellersPage: React.FC = () => {
  const [tab, setTab] = useState<'subs' | 'report'>('subs');

  // Sub-Resellers state
  const [subs, setSubs] = useState<SubReseller[]>([]);
  const [subEarned, setSubEarned] = useState<Record<string, { payin: number; payout: number; total: number }>>({});
  const [loadingSubs, setLoadingSubs] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const formRef = useRef<HTMLFormElement>(null);

  // Commission report state
  const [commissions, setCommissions] = useState<CommissionRow[]>([]);
  const [commSummary, setCommSummary] = useState<CommissionSummary>({ total_amount: 0, payin_amount: 0, payout_amount: 0 });
  const [commTotal, setCommTotal] = useState(0);
  const [loadingComm, setLoadingComm] = useState(false);
  const [commFilter, setCommFilter] = useState({
    type: 'all',
    start_date: dayjs().format('YYYY-MM-DD'),
    end_date: dayjs().format('YYYY-MM-DD'),
    sub_reseller_id: ''
  });
  const [commPage, setCommPage] = useState(1);
  const [commPageSize, setCommPageSize] = useState(20);

  const resellerUser = JSON.parse(localStorage.getItem('reseller_user') || '{}');
  const myFeeRate = parseFloat(resellerUser.fee_rate || 0);
  const myPayoutFeeRate = parseFloat(resellerUser.payout_fee_rate || 0);
  // ⭐ ต้นทุน P2P effective ของเรา (ไม่ตั้ง = ใช้ rate ปกติ) — backend บังคับ floor จริงอีกชั้น
  const myP2pFeeRate = parseFloat((resellerUser.p2p_fee_rate ?? resellerUser.fee_rate) || 0);
  const myP2pPayoutFeeRate = parseFloat((resellerUser.p2p_payout_fee_rate ?? resellerUser.payout_fee_rate) || 0);

  const feeRateNum = parseFloat(form.fee_rate) || 0;
  const payoutFeeRateNum = parseFloat(form.payout_fee_rate) || 0;
  const payinSpreadPreview = Math.round((feeRateNum - myFeeRate) * 100) / 100;
  const payoutSpreadPreview = Math.round((payoutFeeRateNum - myPayoutFeeRate) * 100) / 100;

  // ── Load subs ────────────────────────────────────────────────────────────────
  const loadSubs = async () => {
    try {
      const [subsRes, summaryRes] = await Promise.all([
        merchantApi.get('/reseller/sub-resellers'),
        merchantApi.get('/reseller/sub-commissions/summary'),
      ]);
      setSubs(subsRes.data.data || []);

      // map sub_reseller_id → { payin, payout, total } จาก all-time summary
      const bySub: Record<string, { payin: number; payout: number; total: number }> = {};
      for (const row of (summaryRes.data.data?.by_sub || [])) {
        bySub[row.sub_reseller.id] = {
          payin:  row.payin_amount  || 0,
          payout: row.payout_amount || 0,
          total:  row.total_amount  || 0,
        };
      }
      setSubEarned(bySub);
    } catch {
      // เคลียร์ของเก่า ไม่ให้รายชื่อ/ยอด sub ค้างเมื่อโหลดพลาด
      setSubs([]);
      setSubEarned({});
      toast.error('โหลดรายชื่อ Sub-Reseller ไม่สำเร็จ');
    } finally {
      setLoadingSubs(false);
    }
  };

  // ── Load commission report ────────────────────────────────────────────────────
  const loadCommissions = useCallback(async (page = commPage, pageSize = commPageSize) => {
    setLoadingComm(true);
    try {
      const offset = (page - 1) * pageSize;
      const params: Record<string, string> = {
        limit: String(pageSize),
        offset: String(offset),
        type: commFilter.type
      };
      if (commFilter.start_date) params.start_date = commFilter.start_date;
      if (commFilter.end_date) params.end_date = commFilter.end_date;
      if (commFilter.sub_reseller_id) params.sub_reseller_id = commFilter.sub_reseller_id;

      const res = await merchantApi.get('/reseller/sub-commissions', { params });
      setCommissions(res.data.data || []);
      setCommTotal(res.data.pagination?.total || 0);
      setCommSummary(res.data.summary || { total_amount: 0, payin_amount: 0, payout_amount: 0 });
    } catch {
      // เคลียร์ของเก่า ไม่ให้ยอด commission ค้างเมื่อโหลดพลาด
      setCommissions([]);
      setCommTotal(0);
      setCommSummary({ total_amount: 0, payin_amount: 0, payout_amount: 0 });
      toast.error('โหลดรายงาน Commission ไม่สำเร็จ');
    } finally {
      setLoadingComm(false);
    }
  }, [commFilter, commPage, commPageSize]);

  useEffect(() => { loadSubs(); }, []);

  useEffect(() => {
    if (tab === 'report') loadCommissions(1, commPageSize);
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (tab === 'report') loadCommissions(commPage, commPageSize);
  }, [commPage, commPageSize]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Modal helpers ─────────────────────────────────────────────────────────────
  const openCreateModal = () => { setEditingId(null); setForm(emptyForm); setShowModal(true); };

  const openEditModal = (sub: SubReseller) => {
    setEditingId(sub.id);
    setForm({ email: sub.email, password: '', name: sub.name, company_name: sub.company_name || '', phone: sub.phone || '', fee_rate: String(sub.fee_rate), payout_fee_rate: String(sub.payout_fee_rate || 0), p2p_fee_rate: sub.p2p_fee_rate != null ? String(sub.p2p_fee_rate) : '', p2p_payout_fee_rate: sub.p2p_payout_fee_rate != null ? String(sub.p2p_payout_fee_rate) : '' });
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingId) {
        const updateData: any = { name: form.name, company_name: form.company_name, phone: form.phone, fee_rate: parseFloat(form.fee_rate), payout_fee_rate: parseFloat(form.payout_fee_rate), p2p_fee_rate: form.p2p_fee_rate === '' ? null : parseFloat(form.p2p_fee_rate), p2p_payout_fee_rate: form.p2p_payout_fee_rate === '' ? null : parseFloat(form.p2p_payout_fee_rate) };
        if (form.password) updateData.password = form.password;
        await merchantApi.put(`/reseller/sub-resellers/${editingId}`, updateData);
        toast.success('อัปเดต Sub-Reseller สำเร็จ');
      } else {
        await merchantApi.post('/reseller/sub-resellers', { ...form, fee_rate: parseFloat(form.fee_rate), payout_fee_rate: parseFloat(form.payout_fee_rate), p2p_fee_rate: form.p2p_fee_rate === '' ? null : parseFloat(form.p2p_fee_rate), p2p_payout_fee_rate: form.p2p_payout_fee_rate === '' ? null : parseFloat(form.p2p_payout_fee_rate) });
        toast.success('สร้าง Sub-Reseller สำเร็จ');
      }
      setShowModal(false);
      loadSubs();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'เกิดข้อผิดพลาด');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeactivate = async (sub: SubReseller) => {
    const ok = await swalConfirm({
      title: `ระงับ "${sub.name}"?`,
      text: 'Sub-Reseller จะไม่สามารถเข้าสู่ระบบได้จนกว่าจะเปิดใช้งานอีกครั้ง',
      icon: 'warning',
      confirmText: 'ระงับ',
      confirmColor: '#ef4444',
    });
    if (!ok) return;
    try {
      await merchantApi.post(`/reseller/sub-resellers/${sub.id}/deactivate`);
      toast.success('ระงับ Sub-Reseller สำเร็จ');
      loadSubs();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'เกิดข้อผิดพลาด');
    }
  };

  const handleActivate = async (sub: SubReseller) => {
    try {
      await merchantApi.put(`/reseller/sub-resellers/${sub.id}`, { is_active: true });
      toast.success('เปิดใช้งาน Sub-Reseller สำเร็จ');
      loadSubs();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'เกิดข้อผิดพลาด');
    }
  };

  // ─── Render ──────────────────────────────────────────────────────────────────

  const activeSubs = subs.filter(s => s.is_active).length;
  const totalWallet = subs.reduce((sum, s) => sum + s.wallet_balance, 0);

  const subStatCards: StatCardDef[] = [
    {
      icon: faUsers,
      iconClassName: 'text-white',
      iconStyle: { background: 'var(--theme-primary-gradient)' },
      label: 'ทั้งหมด',
      value: <span style={{ color: 'var(--theme-primary)' }}>{subs.length} คน</span>,
    },
    {
      icon: faCheck,
      iconClassName: 'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400',
      label: 'Active',
      value: <span className="text-green-600 dark:text-green-400">{activeSubs} คน</span>,
    },
    {
      icon: faWallet,
      iconClassName: 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
      label: 'Wallet รวม',
      value: <span className="tabular-nums" style={{ color: 'var(--theme-accent)' }}>฿{formatCurrency(totalWallet)}</span>,
    },
  ];

  const commStatCards: StatCardDef[] = [
    {
      icon: faCoins,
      iconClassName: 'text-white',
      iconStyle: { background: 'var(--theme-primary-gradient)' },
      label: 'รวมทั้งหมด',
      value: <span className="tabular-nums" style={{ color: 'var(--theme-primary)' }}>฿{formatCurrency(commSummary.total_amount)}</span>,
    },
    {
      icon: faArrowDown,
      iconClassName: 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
      label: 'Payin Spread',
      value: <span className="text-blue-600 dark:text-blue-400 tabular-nums">฿{formatCurrency(commSummary.payin_amount)}</span>,
    },
    {
      icon: faMoneyBillWave,
      iconClassName: 'bg-orange-50 dark:bg-orange-900/30 text-orange-500 dark:text-orange-400',
      label: 'Payout Spread',
      value: <span className="text-orange-500 dark:text-orange-400 tabular-nums">฿{formatCurrency(commSummary.payout_amount)}</span>,
    },
  ];

  const subColumns: Column<SubReseller>[] = [
    {
      key: 'name',
      header: 'Sub-Reseller',
      render: (sub) => {
        const initials = sub.name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
        return (
          <div className="flex items-center gap-3">
            <div
              className={`w-9 h-9 rounded-xl flex items-center justify-center text-sm font-bold flex-shrink-0 shadow-sm ${
                sub.is_active ? 'text-white' : 'bg-gray-200 dark:bg-slate-600 text-gray-500 dark:text-slate-300'
              }`}
              style={sub.is_active ? { background: 'var(--theme-primary-gradient)' } : undefined}
            >
              {initials}
            </div>
            <div>
              <p className="font-semibold text-gray-800 dark:text-slate-100 text-sm leading-tight">{sub.name}</p>
              <p className="text-xs text-gray-400 dark:text-slate-500 leading-tight">{sub.email}</p>
              {sub.company_name && (
                <p className="text-[10px] text-gray-400 dark:text-slate-500 mt-0.5">{sub.company_name}</p>
              )}
            </div>
          </div>
        );
      },
    },
    {
      key: 'rate',
      header: 'Rate',
      className: 'text-center',
      render: (sub) => (
        <div>
          <div className="inline-flex flex-col items-center gap-1">
            <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-blue-100 dark:bg-blue-500/15 text-blue-700 dark:text-blue-400">
              {sub.fee_rate}%
            </span>
            <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-orange-100 dark:bg-orange-500/15 text-orange-700 dark:text-orange-400">
              {sub.payout_fee_rate || 0}%
            </span>
          </div>
          <div className="mt-0.5">
            <span className="text-[9px] text-gray-400 dark:text-slate-500">P.In / P.Out</span>
          </div>
        </div>
      ),
    },
    {
      key: 'spread',
      header: 'Spread',
      className: 'text-center',
      render: (sub) => (
        <div className="inline-flex flex-col items-center gap-1">
          <span className={`px-2 py-0.5 rounded-md text-[11px] font-bold ${
            sub.payin_spread > 0
              ? 'bg-blue-50 dark:bg-blue-500/15 text-blue-600 dark:text-blue-400'
              : 'bg-gray-100 dark:bg-slate-700 text-gray-400 dark:text-slate-500'
          }`}>
            {sub.payin_spread >= 0 ? '+' : ''}{sub.payin_spread}%
          </span>
          <span className={`px-2 py-0.5 rounded-md text-[11px] font-bold ${
            sub.payout_spread > 0
              ? 'bg-orange-50 dark:bg-orange-500/15 text-orange-500 dark:text-orange-400'
              : 'bg-gray-100 dark:bg-slate-700 text-gray-400 dark:text-slate-500'
          }`}>
            {sub.payout_spread >= 0 ? '+' : ''}{sub.payout_spread}%
          </span>
        </div>
      ),
    },
    {
      key: 'earned',
      header: 'รายได้ (ทั้งหมด)',
      className: 'text-right',
      render: (sub) => {
        const earned = subEarned[sub.id];
        if (!earned || earned.total <= 0) {
          return <span className="text-gray-300 dark:text-slate-600 text-sm">—</span>;
        }
        return (
          <div>
            <p className="text-sm font-bold text-amber-600 dark:text-amber-400 tabular-nums">+฿{formatCurrency(earned.total)}</p>
            <div className="flex flex-col items-end gap-0.5 mt-0.5">
              {earned.payin > 0 && (
                <span className="text-[10px] text-blue-500 dark:text-blue-400 font-medium tabular-nums">
                  In ฿{formatCurrency(earned.payin)}
                </span>
              )}
              {earned.payout > 0 && (
                <span className="text-[10px] text-orange-500 dark:text-orange-400 font-medium tabular-nums">
                  Out ฿{formatCurrency(earned.payout)}
                </span>
              )}
            </div>
          </div>
        );
      },
    },
    {
      key: 'merchant_count',
      header: 'Merchants',
      className: 'text-center',
      render: (sub) => (
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-blue-50 dark:bg-blue-500/15 text-blue-600 dark:text-blue-400 border border-blue-100 dark:border-blue-500/20">
          <FontAwesomeIcon icon={faUsers} className="text-[10px]" />
          {sub.merchant_count}
        </span>
      ),
    },
    {
      key: 'wallet_balance',
      header: 'Wallet',
      className: 'text-right',
      render: (sub) => (
        <span className="inline-flex items-center gap-1.5 text-sm font-semibold tabular-nums" style={{ color: 'var(--theme-accent)' }}>
          <FontAwesomeIcon icon={faWallet} className="text-xs opacity-70" />
          ฿{formatCurrency(sub.wallet_balance)}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'สถานะ',
      className: 'text-center',
      render: (sub) => (
        <StatusBadge status={sub.is_active ? 'active' : 'inactive'} statusMap={SUB_STATUS_MAP} pulse={sub.is_active} />
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      className: 'text-center',
      render: (sub) => (
        <div className="flex items-center justify-center gap-1.5 opacity-60 group-hover:opacity-100 transition-opacity">
          <button
            onClick={() => openEditModal(sub)}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-blue-500 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors"
            title="Edit"
          >
            <FontAwesomeIcon icon={faEdit} className="text-sm" />
          </button>
          {sub.is_active ? (
            <button
              onClick={() => handleDeactivate(sub)}
              className="w-8 h-8 rounded-lg flex items-center justify-center text-red-400 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
              title="Deactivate"
            >
              <FontAwesomeIcon icon={faBan} className="text-sm" />
            </button>
          ) : (
            <button
              onClick={() => handleActivate(sub)}
              className="w-8 h-8 rounded-lg flex items-center justify-center text-green-500 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/30 transition-colors"
              title="Activate"
            >
              <FontAwesomeIcon icon={faCheck} className="text-sm" />
            </button>
          )}
        </div>
      ),
    },
  ];

  const subsFooter = (
    <tr>
      <td colSpan={subColumns.length} className="px-5 py-3">
        <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500 dark:text-slate-400">
          <span className="font-medium text-gray-600 dark:text-slate-300">{subs.length} Sub-Reseller</span>
          <span className="w-px h-4 bg-gray-200 dark:bg-slate-600" />
          <span>Active: <strong className="text-green-600 dark:text-green-400">{activeSubs}</strong></span>
          <span>Inactive: <strong className="text-gray-500 dark:text-slate-400">{subs.length - activeSubs}</strong></span>
          <span className="w-px h-4 bg-gray-200 dark:bg-slate-600" />
          <span>Wallet รวม: <strong className="tabular-nums" style={{ color: 'var(--theme-accent)' }}>฿{formatCurrency(totalWallet)}</strong></span>
        </div>
      </td>
    </tr>
  );

  const commColumns: Column<CommissionRow>[] = [
    {
      key: 'created_at',
      header: 'วันที่',
      render: (row) => (
        <span className="text-xs text-gray-500 dark:text-slate-400 whitespace-nowrap font-mono">
          {dayjs(row.created_at).format('DD/MM/YY HH:mm')}
        </span>
      )
    },
    {
      key: 'sub_reseller',
      header: 'Sub-Reseller',
      render: (row) => (
        <div>
          <p className="text-sm font-semibold text-gray-800 dark:text-slate-100">{row.sub_reseller?.name || '—'}</p>
          <p className="text-xs text-gray-400 dark:text-slate-500">{row.sub_reseller?.email}</p>
        </div>
      )
    },
    {
      key: 'merchant',
      header: 'Merchant',
      render: (row) => (
        <span className="text-sm text-gray-600 dark:text-slate-300">{row.merchant?.name || '—'}</span>
      )
    },
    {
      key: 'order',
      header: 'Order / Payout #',
      render: (row) => (
        <>
          {row.order && (
            <span className="font-mono text-xs bg-blue-50 dark:bg-blue-500/15 text-blue-700 dark:text-blue-400 px-2 py-1 rounded-lg">
              {row.order.order_number}
            </span>
          )}
          {row.payout && (
            <span className="font-mono text-xs bg-orange-50 dark:bg-orange-500/15 text-orange-700 dark:text-orange-400 px-2 py-1 rounded-lg">
              {row.payout.payout_number}
            </span>
          )}
        </>
      )
    },
    {
      key: 'transaction_type',
      header: 'ประเภท',
      render: (row) => (
        <StatusBadge status={row.transaction_type} statusMap={COMM_TYPE_MAP} />
      )
    },
    {
      key: 'base_amount',
      header: 'ยอด Base',
      render: (row) => (
        <span className="text-sm text-gray-600 dark:text-slate-300 font-mono tabular-nums">
          {row.order ? `฿${formatCurrency(row.order.base_amount)}` : row.payout ? `฿${formatCurrency(row.payout.amount)}` : '—'}
        </span>
      )
    },
    {
      key: 'amount',
      header: 'Spread ที่ได้',
      render: (row) => (
        <span className="text-sm font-bold tabular-nums" style={{ color: 'var(--theme-accent)' }}>
          +฿{formatCurrency(row.amount)}
        </span>
      )
    }
  ];

  return (
    <div className="max-w-full">
      {/* ── Header ── */}
      <UiPageHeader
        icon={faUserShield}
        title="Sub-Resellers"
        subtitle="จัดการ Sub-Reseller และรายงาน Commission"
        below={
          <div className="flex flex-wrap items-center gap-2 mt-1">
            <span className="text-white/70 text-xs">Rate ของคุณ:</span>
            <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-white/20 text-white border border-white/20">
              P.In {myFeeRate}%
            </span>
            <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-white/20 text-white border border-white/20">
              P.Out {myPayoutFeeRate}%
            </span>
          </div>
        }
        actions={tab === 'subs' ? (
          <UiButton icon={faPlus} onClick={openCreateModal}>Add Sub-Reseller</UiButton>
        ) : undefined}
      />

      {/* ── Stats ── */}
      <UiStatsGrid cols={3} cards={subStatCards} loading={loadingSubs} />

      {/* ── Tab segmented control ── */}
      <div className="mb-6">
        <div className="inline-flex items-center bg-gray-100 dark:bg-slate-700 p-1 rounded-xl">
          <button
            onClick={() => setTab('subs')}
            className={`${TAB_BTN_BASE} ${tab === 'subs' ? TAB_BTN_ACTIVE : TAB_BTN_INACTIVE}`}
          >
            <FontAwesomeIcon icon={faUsers} className="text-xs" />
            Sub-Resellers
          </button>
          <button
            onClick={() => setTab('report')}
            className={`${TAB_BTN_BASE} ${tab === 'report' ? TAB_BTN_ACTIVE : TAB_BTN_INACTIVE}`}
          >
            <FontAwesomeIcon icon={faChartBar} className="text-xs" />
            Commission Report
          </button>
        </div>
      </div>

      {/* ══════════════ TAB: Sub-Resellers ══════════════ */}
      {tab === 'subs' && (
        <>
          <DataTable
            data={subs}
            columns={subColumns}
            loading={loadingSubs}
            emptyMessage="ยังไม่มี Sub-Reseller"
            emptyIcon={<FontAwesomeIcon icon={faUserShield} className="text-5xl text-gray-200 dark:text-slate-600" />}
            keyExtractor={(sub) => sub.id}
            rowClassName="group"
            pagination={false}
            footer={subsFooter}
            hoverable
          />
          {!loadingSubs && subs.length === 0 && (
            <div className="flex flex-col items-center gap-2 mt-4">
              <p className="text-sm text-gray-400 dark:text-slate-500">กด "Add Sub-Reseller" เพื่อเริ่มต้น</p>
              <UiButton icon={faPlus} onClick={openCreateModal}>Add Sub-Reseller</UiButton>
            </div>
          )}
        </>
      )}

      {/* ══════════════ TAB: Commission Report ══════════════ */}
      {tab === 'report' && (
        <>
          <UiFilterPanel
            dateFrom={commFilter.start_date}
            dateTo={commFilter.end_date}
            onDateFromChange={(v) => setCommFilter(f => ({ ...f, start_date: v }))}
            onDateToChange={(v) => setCommFilter(f => ({ ...f, end_date: v }))}
            onSearch={() => { setCommPage(1); loadCommissions(1, commPageSize); }}
            onReset={() => {
              setCommFilter({ type: 'all', start_date: dayjs().format('YYYY-MM-DD'), end_date: dayjs().format('YYYY-MM-DD'), sub_reseller_id: '' });
              setCommPage(1);
            }}
            loading={loadingComm}
            actions={
              <UiButton variant="secondary" icon={faRotate} onClick={() => loadCommissions(commPage, commPageSize)}>
                Refresh
              </UiButton>
            }
          >
            <UiSelect
              label="ประเภท"
              value={commFilter.type}
              onChange={e => setCommFilter(f => ({ ...f, type: e.target.value }))}
              wrapperClassName="flex-1 min-w-[150px] sm:max-w-[180px]"
            >
              <option value="all">ทั้งหมด</option>
              <option value="payin">Payin (P.In)</option>
              <option value="payout">Payout (P.Out)</option>
            </UiSelect>
            <UiSelect
              label="Sub-Reseller"
              value={commFilter.sub_reseller_id}
              onChange={e => setCommFilter(f => ({ ...f, sub_reseller_id: e.target.value }))}
              wrapperClassName="flex-1 min-w-[180px] sm:max-w-[220px]"
            >
              <option value="">ทุก Sub-Reseller</option>
              {subs.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </UiSelect>
          </UiFilterPanel>

          <UiStatsGrid cols={3} cards={commStatCards} />

          <DataTable
            data={commissions}
            columns={commColumns}
            loading={loadingComm}
            emptyMessage="ไม่พบข้อมูล Commission"
            emptyIcon={<FontAwesomeIcon icon={faCoins} className="text-5xl text-gray-200 dark:text-slate-600" />}
            keyExtractor={(row) => row.id}
            hoverable
            serverPagination={{
              currentPage: commPage,
              totalItems: commTotal,
              pageSize: commPageSize,
              pageSizeOptions: [10, 20, 50, 100],
              onPageChange: (page) => setCommPage(page),
              onPageSizeChange: (size) => { setCommPageSize(size); setCommPage(1); }
            }}
          />
        </>
      )}

      {/* ══════════════ Modal: Create / Edit ══════════════ */}
      <UiModal
        open={showModal}
        onClose={() => setShowModal(false)}
        title={editingId ? 'แก้ไข Sub-Reseller' : 'สร้าง Sub-Reseller ใหม่'}
        icon={faUserShield}
        iconStyle={{ color: 'var(--theme-primary)' }}
        size="lg"
        scrollable
        closeOnBackdrop={false}
        footer={
          <div className="flex gap-3">
            <UiButton variant="secondary" onClick={() => setShowModal(false)} disabled={submitting} className="flex-1 justify-center">
              Cancel
            </UiButton>
            <UiButton loading={submitting} onClick={() => formRef.current?.requestSubmit()} className="flex-1 justify-center">
              {submitting ? 'Saving...' : editingId ? 'Update' : 'Create'}
            </UiButton>
          </div>
        }
      >
        <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
          {!editingId && (
            <UiInput
              label="Email"
              type="email"
              required
              value={form.email}
              onChange={e => setForm({ ...form, email: e.target.value })}
            />
          )}
          <UiInput
            label={editingId ? 'Password (เว้นว่างถ้าไม่เปลี่ยน)' : 'Password'}
            type="password"
            required={!editingId}
            value={form.password}
            onChange={e => setForm({ ...form, password: e.target.value })}
          />
          <UiInput
            label="ชื่อ"
            type="text"
            required
            value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })}
          />
          <UiInput
            label="บริษัท"
            type="text"
            value={form.company_name}
            onChange={e => setForm({ ...form, company_name: e.target.value })}
          />
          <UiInput
            label="เบอร์โทร"
            type="text"
            value={form.phone}
            onChange={e => setForm({ ...form, phone: e.target.value })}
          />
          <div>
            <UiInput
              label="Payin Fee Rate (%)"
              type="number"
              step="0.01"
              min={myFeeRate}
              required
              value={form.fee_rate}
              onChange={e => setForm({ ...form, fee_rate: e.target.value })}
              placeholder={`ต้องไม่น้อยกว่า ${myFeeRate}%`}
            />
            <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">
              ต้องไม่น้อยกว่า {myFeeRate}%
              {payinSpreadPreview > 0 && <span className="ml-2 text-orange-500 dark:text-orange-400 font-medium">Spread ที่คุณได้: +{payinSpreadPreview}%</span>}
            </p>
          </div>
          <div>
            <UiInput
              label="Payout Fee Rate (%)"
              type="number"
              step="0.01"
              min={myPayoutFeeRate}
              required
              value={form.payout_fee_rate}
              onChange={e => setForm({ ...form, payout_fee_rate: e.target.value })}
              placeholder={`ต้องไม่น้อยกว่า ${myPayoutFeeRate}%`}
            />
            <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">
              ต้องไม่น้อยกว่า {myPayoutFeeRate}%
              {payoutSpreadPreview > 0 && <span className="ml-2 text-orange-500 dark:text-orange-400 font-medium">Spread ที่คุณได้: +{payoutSpreadPreview}%</span>}
            </p>
          </div>
          <UiInput
            label="P2P Payin Rate (%)"
            type="number"
            step="0.01"
            min={myP2pFeeRate}
            value={form.p2p_fee_rate}
            onChange={e => setForm({ ...form, p2p_fee_rate: e.target.value })}
            placeholder={`เว้นว่าง = ใช้ rate ปกติ (ขั้นต่ำ ${myP2pFeeRate}%)`}
          />
          <div>
            <UiInput
              label="P2P Payout Rate (%)"
              type="number"
              step="0.01"
              min={myP2pPayoutFeeRate}
              value={form.p2p_payout_fee_rate}
              onChange={e => setForm({ ...form, p2p_payout_fee_rate: e.target.value })}
              placeholder={`เว้นว่าง = ใช้ rate ปกติ (ขั้นต่ำ ${myP2pPayoutFeeRate}%)`}
            />
            <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">ต้นทุน P2P ของ Sub — ใช้คิดค่าคอมเฉพาะรายการ P2P</p>
          </div>
        </form>
      </UiModal>
    </div>
  );
};

export default ResellerSubResellersPage;
